import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { confirmDialog } from '@/lib/confirmDialog'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Loading from '@/components/common/Loading'
import { toast } from 'sonner'

function DispatchSend() {
  const location = useLocation()
  const navigate = useNavigate()
  
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [undoing, setUndoing] = useState(false)
  
  // Get dispatch data from navigation state
  const dispatchData = location.state

  useEffect(() => {
    if (!dispatchData) {
      navigate('/dashboard')
    }
  }, [dispatchData, navigate])

  const handleSend = async () => {
    setSending(true)
    setError(null)
    
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        throw new Error('Not authenticated')
      }
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          teamId: dispatchData.teamId,
          userId: session.user.id,
          subjectLine: dispatchData.subjectLine,
          emailContent: dispatchData.emailContent,
          wikiItems: dispatchData.wikiItems || [],
          rawNotes: dispatchData.rawNotes || null,
          draftId: dispatchData.draftId || null
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send dispatch')
      }

      setResult(data)
      setSent(true)
      // Success toast with context-aware messaging
      if (data.wiki_auto_applied > 0) {
        const createdCount = data.wiki_created || 0
        const updatedCount = data.wiki_updated || 0
        let message = 'Dispatch sent! Wiki '
        if (createdCount > 0 && updatedCount > 0) {
          message += `updated: ${createdCount} added, ${updatedCount} updated.`
        } else if (updatedCount > 0) {
          message += `updated: ${updatedCount} item${updatedCount === 1 ? '' : 's'} modified.`
        } else {
          message += `updated with ${createdCount} new item${createdCount === 1 ? '' : 's'}.`
        }
        toast.success(message, {
          action: {
            label: 'View Wiki',
            onClick: () => navigate('/wiki')
          }
        })
      } else if (data.wiki_queued > 0) {
        toast.success('Dispatch sent! Some wiki items need your review.', {
          action: {
            label: 'Review',
            onClick: () => navigate('/wiki/review')
          }
        })
      } else {
        toast.success('Dispatch sent successfully!')
      }
    } catch (err) {
      console.error('Error sending dispatch:', err)
      setError(err.message)
    } finally {
      setSending(false)
    }
  }

  if (!dispatchData) {
    return null
  }

  if (sending) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <Card className="max-w-md text-center">
          <div className="mb-4">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-brand-green border-t-transparent"></div>
          </div>
          <h2 className="font-heading text-xl font-bold mb-2">
            Sending Dispatch...
          </h2>
          <p className="text-brand-slate">
            This may take a moment. We're sending emails to your parents.
          </p>
        </Card>
      </div>
    )
  }

  if (sent && result) {
    return (
      <div className="min-h-screen bg-brand-cream py-8">
        <Container>
          <div className="max-w-2xl mx-auto">
            <Card>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h1 className="font-heading text-3xl font-bold mb-2">
                  Dispatch Sent! 🎉
                </h1>
                <p className="text-brand-slate text-lg">
                  {result.message}
                </p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-green-900">Successfully Sent</span>
                    <span className="text-2xl font-bold text-green-600">{result.success_count}</span>
                  </div>
                  {result.failure_count > 0 && (
                    <div className="flex items-center justify-between text-red-600">
                      <span className="font-medium">Failed</span>
                      <span className="text-xl font-bold">{result.failure_count}</span>
                    </div>
                  )}
                </div>

          {/* Wiki Auto-Applied Items */}
          {result.wiki_auto_applied > 0 && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-green-600 text-xl">✓</span>
                <h3 className="font-medium text-green-900">
                  Wiki automatically updated
                </h3>
              </div>
              <p className="text-sm text-green-800 mb-3">
                {(() => {
                  const created = result.wiki_created || 0
                  const updated = result.wiki_updated || 0
                  if (created > 0 && updated > 0) {
                    return `Added ${created} new item${created === 1 ? '' : 's'}, updated ${updated} existing`
                  } else if (updated > 0) {
                    return `Updated ${updated} existing item${updated === 1 ? '' : 's'}`
                  } else {
                    return `Added ${created} new item${created === 1 ? '' : 's'}`
                  }
                })()}
                {result.wiki_changes?.length > 0 && (
                  <span>: {result.wiki_changes.map(c => c.title).join(', ')}</span>
                )}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => navigate('/wiki')}
                  className="flex-1"
                >
                  View Wiki
                </Button>
                <Button
                  variant="secondary"
                  onClick={async () => {
                    const confirmed = await confirmDialog({
                      title: 'Undo all wiki changes?',
                      message: `This will undo ${result.wiki_auto_applied} auto-applied wiki ${result.wiki_auto_applied === 1 ? 'change' : 'changes'} from this dispatch.`,
                      confirmText: 'Undo All'
                    })
                    if (!confirmed) return
                    
                    setUndoing(true)
                    try {
                      const { data: { user } } = await supabase.auth.getUser()
                      let undoneCount = 0
                      
for (const change of result.wiki_changes || []) {
                                        // Find the change log entry by wiki_item_id and dispatch_id
                                        const { data: changeLogs } = await supabase
                                          .from('wiki_change_log')
                                          .select('id')
                                          .eq('wiki_item_id', change.id)
                                          .eq('dispatch_id', result.dispatch_id)
                                          .is('undone_at', null)
                                          .limit(1)
                                        
                                        if (changeLogs && changeLogs.length > 0) {
                                          const { data: undoResult } = await supabase.rpc('undo_wiki_change', {
                                            p_change_id: changeLogs[0].id,
                                            p_team_id: dispatchData.teamId,
                                            p_force: false
                                          })
                                          if (undoResult?.success) undoneCount++
                                        }
                                      }
                      
                      toast.success(`Undone ${undoneCount} wiki change${undoneCount === 1 ? '' : 's'}`)
                      // Update result to reflect undo
                      setResult(prev => ({
                        ...prev,
                        wiki_auto_applied: 0,
                        wiki_changes: []
                      }))
                    } catch (err) {
                      console.error('Error undoing wiki changes:', err)
                      toast.error('Failed to undo some changes')
                    } finally {
                      setUndoing(false)
                    }
                  }}
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                  disabled={undoing}
                >
                  {undoing ? 'Undoing...' : 'Undo All'}
                </Button>
              </div>
            </div>
          )}

          {/* Wiki Queued Items (need review) */}
          {result.wiki_queued > 0 && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-900 mb-2">
                <span className="font-medium">{result.wiki_queued}</span> {result.wiki_queued === 1 ? 'item' : 'items'} queued for your review
                <span className="text-amber-700 ml-1">(lower confidence)</span>
              </p>
              <Button
                variant="secondary"
                onClick={() => navigate('/wiki/review')}
                className="w-full"
              >
                Review Suggestions →
              </Button>
            </div>
          )}

          {/* Legacy fallback for wiki_suggestions_count */}
          {!result.wiki_auto_applied && !result.wiki_queued && result.wiki_suggestions_count > 0 && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-medium text-blue-900 mb-2">
                🤖 AI found {result.wiki_suggestions_count} {result.wiki_suggestions_count === 1 ? 'item' : 'items'} to add to your wiki
              </h3>
              <p className="text-sm text-blue-800 mb-3">
                These suggestions have been saved. You can review them now or anytime from your Team Wiki.
              </p>
              <Button
                variant="primary"
                onClick={() => navigate('/wiki')}
                className="w-full"
              >
                Go to Team Wiki →
              </Button>
            </div>
          )}

                <div className="p-4 bg-brand-cream border border-border rounded-lg">
                  <p className="text-sm text-brand-slate mb-2">
                    <strong>Subject:</strong> {dispatchData.subjectLine}
                  </p>
                  <p className="text-sm text-brand-slate">
                    <strong>Portal:</strong>{' '}
                    <a 
                      href={result.portal_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-brand-green hover:underline"
                    >
                      {result.portal_url}
                    </a>
                  </p>
                </div>

                {result.details && result.details.some(d => d.status === 'failed') && (
                  <details className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <summary className="font-medium text-red-900 cursor-pointer">
                      View failed emails ({result.failure_count})
                    </summary>
                    <ul className="mt-2 space-y-1 text-sm text-red-700">
                      {result.details
                        .filter(d => d.status === 'failed')
                        .map((detail, i) => (
                          <li key={i}>
                            {detail.email}: {detail.error}
                          </li>
                        ))}
                    </ul>
                  </details>
                )}
              </div>

              <div className="flex gap-4">
                <Button
                  variant="secondary"
                  onClick={() => navigate('/dashboard')}
                  className="flex-1"
                >
                  Back to Dashboard
                </Button>
                <Button
                  variant="primary"
                  onClick={() => window.open(result.portal_url, '_blank')}
                  className="flex-1"
                >
                  View Portal →
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigate('/wiki')}
                  className="flex-1"
                >
                  Review Wiki →
                </Button>
              </div>
            </Card>

            <div className="mt-6 text-center text-sm text-brand-slate">
              <p>
                Parents can view this dispatch and all team information at the portal link above.
              </p>
            </div>
          </div>
        </Container>
      </div>
    )
  }

  // Confirmation screen (before sending)
  return (
    <div className="min-h-screen bg-brand-cream py-8">
      <Container>
        <div className="max-w-2xl mx-auto">
          <Card>
            <div className="text-center mb-6">
              <h1 className="font-heading text-3xl font-bold mb-2">
                Ready to Send?
              </h1>
              <p className="text-brand-slate">
                Review the details before sending your dispatch
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="p-4 bg-brand-cream border border-border rounded-lg">
                <p className="text-sm font-medium text-brand-charcoal mb-1">Subject</p>
                <p className="text-brand-slate">{dispatchData.subjectLine}</p>
              </div>

              <div className="p-4 bg-brand-cream border border-border rounded-lg">
                <p className="text-sm font-medium text-brand-charcoal mb-2">Preview</p>
                <div 
                  className="prose prose-sm max-w-none text-brand-slate"
                  dangerouslySetInnerHTML={{ 
                    __html: dispatchData.emailContent 
                  }}
                />
              </div>

              {dispatchData.wikiItems && dispatchData.wikiItems.length > 0 && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900">
                    📝 {dispatchData.wikiItems.length} {dispatchData.wikiItems.length === 1 ? 'wiki item' : 'wiki items'} will be added
                  </p>
                </div>
              )}

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">
                    <strong>Error:</strong> {error}
                  </p>
                  {error.includes('API key') && (
                    <p className="text-xs text-red-600 mt-2">
                      Make sure your Postmark API key is configured in Supabase Edge Function secrets.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-4">
              <Button
                variant="secondary"
                onClick={() => navigate(-1)}
                className="flex-1"
              >
                Go Back
              </Button>
              <Button
                variant="primary"
                onClick={handleSend}
                className="flex-1"
                disabled={sending}
              >
                {sending ? 'Sending...' : 'Send Dispatch'}
              </Button>
            </div>

            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-900">
                <strong>Note:</strong> This will send emails to all parents who have not opted out 
                and clear your draft notes.
              </p>
            </div>
          </Card>
        </div>
      </Container>
    </div>
  )
}

export default DispatchSend


